import * as globals from './globals';

function getPositionX(event) {
  // check if touch event, otherwise mouse event
  if (event.changedTouches && !!event.changedTouches.length) {
    return event.changedTouches[0].pageX;
  }

  return event.pageX;
}

class DynamicTable {
  constructor(tableElement) {
    this.tableElement = tableElement;
    this.navigationElement = tableElement.querySelector(globals.NAVIGATION_SELECTOR);
    this.tableHeaderTitleElement = tableElement.querySelector(globals.HEADER_TITLE_SELECTOR);
    this.table = tableElement.querySelector(globals.TABLE_SELECTOR);
    this.container = tableElement.querySelector(globals.TABLE_CONTAINER_SELECTOR);
    this.nextButton = tableElement.querySelector(globals.NAVIGATION_NEXT_BUTTON_SELECTOR);
    this.prevButton = tableElement.querySelector(globals.NAVIGATION_PREV_BUTTON_SELECTOR);
    this.thead = this.table.querySelector('thead');
    this.columns = Array.from(tableElement.querySelectorAll(globals.HEADER_CELL_TAG));
    this.rows = Array.from(tableElement.querySelectorAll(globals.BODY_ROW_TAG));
    this.shadowElement = tableElement.querySelector(globals.SHADOW_SELECTOR);

    this.isDownEvent = false;
    this.isMoveEvent = false;
    this.savedPositionX = 0;
    this.scrollLeft = 0;

    this.cumulativeWidths = [];
    this.firstColumnWidth = 0;
    this.otherColumnsWidth = 0;
    this.currentIndex = 1;
    this.programmaticScroll = false;
    this.programmaticTimer = null;
    this.scrollTimer = null;
    this.lastScrollLeft = 0;

    // Add scroll event listener for snap-to-column behavior after user scrolling
    this.container.addEventListener('scroll', () => {
      // Don't snap if this is a programmatic scroll (from prev/next buttons)
      if (this.programmaticScroll) {
        return;
      }

      // Clear any existing timer
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }

      // Set a new timer to detect when scrolling has stopped
      this.scrollTimer = setTimeout(() => {
        this.snapToNearestColumnAfterUserScroll();
        this.scrollTimer = null;
      }, 150); // Wait 150ms after scrolling stops
    });

    this.nextButton.addEventListener('click', () => {
      this.calculateWidth();
      let currentIndex = typeof this.currentIndex === 'number'
        ? this.currentIndex
        : this.getMostVisibleColumn();
      if (currentIndex <= 0 || currentIndex >= this.columns.length) { currentIndex = 1; }
      this.scrollToColumn(currentIndex + 1);
      this.updateNavigation();
    });

    this.prevButton.addEventListener('click', () => {
      this.calculateWidth();
      let cur = typeof this.currentIndex === 'number'
        ? this.currentIndex
        : this.getMostVisibleColumn();
      if (cur <= 0 || cur >= this.columns.length) cur = 1;
      this.scrollToColumn(cur - 1);
      this.updateNavigation();
    });

    // --- Initialize ---
    this.calculateWidth();
    this.lastScrollLeft = this.container.scrollLeft; // Initialize scroll position

    window.addEventListener('resize', () => {
      this.snapToNearestColumn();
      this.calculateWidth();
      this.updateNavigation();
    });

    // Adjust <thead> position on scroll
    window.addEventListener('scroll', () => {
      this.updateTableHeaderPosition();
    });

    // Adjust <thead> position on initialization
    this.updateTableHeaderPosition();

    window.addEventListener('load', () => {
      this.updateNavigation();
      this.snapToNearestColumn();
    });
  }

  moveHeaderTitle() {
    if (this.tableHeaderTitleElement) {
      this.navigationElement.prepend(this.tableHeaderTitleElement);
    }
  }

  calculateWidth() {
    const screenWidth = window.innerWidth;
    const columnsCount = this.columns.length;
    let firstColumnWidthValue;
    let otherColumnsWidthValue;

    if (screenWidth < globals.SCREENSIZE_SM) {
      const halfWidth = this.container.clientWidth / 2;
      this.firstColumnWidth = halfWidth;
      this.otherColumnsWidth = halfWidth;

      const containerRect = this.container.getBoundingClientRect();

      if (this.shadowElement) {
        this.shadowElement.style.left = `${this.firstColumnWidth + containerRect.x}px`;
        this.shadowElement.style.height = `${this.container.clientHeight}px`;
      }

      firstColumnWidthValue = `${halfWidth}px`;
      otherColumnsWidthValue = `${halfWidth}px`;

      this.cumulativeWidths = [];
      let sum = 0;
      this.columns.forEach((element) => {
        this.cumulativeWidths.push(sum);
        sum += element.offsetWidth || Math.round(element.getBoundingClientRect().width);
      });
    } else {
      if (columnsCount === 2) {
        this.firstColumnWidth = this.tableElement.clientWidth * 0.55;
        this.otherColumnsWidth = this.tableElement.clientWidth * 0.45;
      } else {
        this.firstColumnWidth = this.tableElement.clientWidth * 0.4;
        this.otherColumnsWidth = (this.tableElement.clientWidth - this.firstColumnWidth) / (columnsCount - 1);
      }

      firstColumnWidthValue = `${this.firstColumnWidth}px`;
      otherColumnsWidthValue = `${this.otherColumnsWidth}px`;
    }

    this.tableElement.style = `--at-dynamic-table--first-column-width: ${firstColumnWidthValue}; --at-dynamic-table--other-columns-width: ${otherColumnsWidthValue};`;

    if (screenWidth >= globals.SCREENSIZE_SM || columnsCount <= 2) {
      this.container.classList.add('at-border-radius-top-right--rounded', 'at-border-radius-top-left--rounded');
      this.showNavigation = false;
      this.navigationElement.style.display = 'none';
      this.container.classList.remove('at-border-top--none');

      if (this.shadowElement) {
        this.shadowElement.style.display = 'none';
      }
    } else if (columnsCount > 2) {
      this.showNavigation = true;
      this.container.classList.remove('at-border-radius-top-right--rounded', 'at-border-radius-top-left--rounded');
      this.navigationElement.style.display = 'flex';
      this.container.classList.add('at-border-top--none');

      if (this.shadowElement) {
        this.shadowElement.style.display = 'flex';
      }

      this.updateNavigationAriaLabels(); // Update aria-labels during initialization
    }
  }

  getContainerWidth() {
    return this.container.offsetWidth || this.container.clientWidth;
  }

  scrollToColumn(index) {
    const maxIndex = Math.max(0, this.columns.length - 1);
    const currentIndex = Math.max(0, Math.min(index, maxIndex));

    const visibleWidth = this.container.clientWidth;
    const totalWidth = (this.cumulativeWidths.at(-1) || 0) + this.otherColumnsWidth;
    let target = 0;
    if (currentIndex !== 0) {
      const colLeft = this.cumulativeWidths[currentIndex] || 0;
      // Always align the column's left edge to the start of scrollable area (after sticky column)
      target = colLeft - this.firstColumnWidth;
    }

    // Ensure we don't scroll beyond the maximum scrollLeft
    const maxScrollLeft = Math.max(0, totalWidth - visibleWidth);
    target = Math.min(target, maxScrollLeft);

    // Mark that this scroll was triggered programmatically so the scroll debounce
    // doesn't immediately snap us back while the smooth scroll is happening.
    this.programmaticScroll = true;
    if (this.programmaticTimer) clearTimeout(this.programmaticTimer);
    // Update currentIndex now so repeated clicks behave deterministically
    this.currentIndex = currentIndex;

    this.scrollLeft = target;

    // Enable smooth scrolling for this programmatic scroll
    this.container.classList.add('smooth-scroll');

    this.programmaticTimer = setTimeout(() => {
      this.programmaticScroll = false;
      this.programmaticTimer = null;
      this.container.classList.remove('smooth-scroll');
      this.updateNavigationAriaLabels(); // Update aria-labels after scrolling
    }, 420);
    this.container.scrollTo({ left: target, behavior: 'smooth' });
  }

  // Determine which column is currently most visible in the container viewport.
  // This is robust for touch dragging where a column might be partially visible.
  getMostVisibleColumn() {
    const scrollLeft = this.container.scrollLeft;
    const visibleWidth = this.getContainerWidth();
    const visibleRight = scrollLeft + visibleWidth;
    let bestIndex = 0;
    let bestVisiblePx = -1;

    const viewportStartExcludingSticky = scrollLeft + this.firstColumnWidth;

    for (let i = 0; i < this.columns.length; i += 1) {
      const left = this.cumulativeWidths[i] || 0;
      const right = left + this.otherColumnsWidth;

      let visibleLeft; let
        visibleRightEdge;
      if (i === 0) {
        // First column is sticky and therefore always visible (at least the sticky area).
        visibleLeft = left;
        visibleRightEdge = Math.min(right, visibleRight);
      } else {
        visibleLeft = Math.max(left, viewportStartExcludingSticky);
        visibleRightEdge = Math.min(right, visibleRight);
      }

      const visiblePx = Math.max(0, visibleRightEdge - visibleLeft);

      if (visiblePx > bestVisiblePx) {
        bestVisiblePx = visiblePx;
        bestIndex = i;
      }
    }
    return bestIndex;
  }

  updateNavigation() {
    const index = this.currentIndex === 0 ? 1 : this.currentIndex;

    if (this.showNavigation) {
      if (index <= 1) {
        this.prevButton.setAttribute('disabled', true);
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (index >= this.columns.length - 1) {
        this.nextButton.setAttribute('disabled', true);
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    } else {
      this.scrollLeft = 0;
    }
  }

  updateNavigationAriaLabels() {
    const prevColumnIndex = this.currentIndex - 1;
    const nextColumnIndex = this.currentIndex + 1;

    if (prevColumnIndex >= 0) {
      const prevColumnName = this.columns[prevColumnIndex].textContent.trim();
      this.prevButton.setAttribute(
        'aria-label',
        `Previous column ${prevColumnName}`,
      );
    } else {
      this.prevButton.setAttribute('aria-label', 'No previous column');
    }

    if (nextColumnIndex < this.columns.length) {
      const nextColumnName = this.columns[nextColumnIndex].textContent.trim();
      this.nextButton.setAttribute(
        'aria-label',
        `Next column ${nextColumnName}`,
      );
    } else {
      this.nextButton.setAttribute('aria-label', 'No next column');
    }
  }

  // --- Snap to the most-visible column ---
  snapToNearestColumn() {
    this.calculateWidth();
    const idx = this.getMostVisibleColumn();
    // Each column should snap so its left edge aligns right after the sticky first column
    let target = 0;
    if (idx !== 0) {
      const colLeft = this.cumulativeWidths[idx] || 0;
      // Always align the column's left edge to the start of scrollable area
      target = colLeft - this.firstColumnWidth;
    }

    // Enable smooth scrolling for this programmatic scroll
    this.container.classList.add('smooth-scroll');
    this.container.scrollTo({ left: target, behavior: 'smooth' });

    // update the canonical currentIndex so subsequent clicks move relative to this
    this.currentIndex = idx;
    // ensure programmatic flag doesn't interfere with explicit snap
    this.programmaticScroll = true;
    if (this.programmaticTimer) clearTimeout(this.programmaticTimer);
    this.programmaticTimer = setTimeout(() => {
      this.programmaticScroll = false;
      this.container.classList.remove('smooth-scroll');
      this.programmaticTimer = null;
    }, 420);
  }

  // --- Snap after user scrolling (considers scroll direction) ---
  snapToNearestColumnAfterUserScroll() {
    if (!this.showNavigation) {
      return;
    }

    const currentScrollLeft = this.container.scrollLeft;
    const scrollDirection = currentScrollLeft - this.lastScrollLeft;
    this.lastScrollLeft = currentScrollLeft;

    this.calculateWidth();

    const scrollLeft = this.container.scrollLeft;
    let targetIdx = this._findClosestColumnIndex(scrollLeft);

    targetIdx = this._adjustIndexForScrollDirection(targetIdx, scrollLeft, scrollDirection);

    const target = this._getTargetScrollPosition(targetIdx);

    this._performSnapScroll(target, targetIdx);
  }

  _findClosestColumnIndex(scrollLeft) {
    let targetIdx = 1;
    let minDistance = Infinity;
    for (let i = 1; i < this.columns.length; i += 1) {
      const colLeft = this.cumulativeWidths[i] || 0;
      const targetScrollPosition = colLeft - this.firstColumnWidth;
      const distance = Math.abs(scrollLeft - targetScrollPosition);
      if (distance < minDistance) {
        minDistance = distance;
        targetIdx = i;
      }
    }
    return targetIdx;
  }

  _adjustIndexForScrollDirection(targetIdx, scrollLeft, scrollDirection) {
    let adjustedIndex = targetIdx; // Create a new variable to avoid reassigning the parameter

    if (Math.abs(scrollDirection) > 10) {
      const currentColLeft = this.cumulativeWidths[adjustedIndex] || 0;
      const currentTargetScroll = currentColLeft - this.firstColumnWidth;
      if (Math.abs(scrollLeft - currentTargetScroll) > 5) {
        if (scrollDirection > 0 && adjustedIndex < this.columns.length - 1) {
          adjustedIndex = DynamicTable._incrementIndexIfNeeded(adjustedIndex, scrollLeft, currentTargetScroll);
        } else if (scrollDirection < 0 && adjustedIndex > 1) {
          adjustedIndex = DynamicTable._decrementIndexIfNeeded(adjustedIndex, scrollLeft, currentTargetScroll);
        }
      }
    }

    return adjustedIndex; // Return the new variable
  }

  static _incrementIndexIfNeeded(targetIdx, scrollLeft, currentTargetScroll) {
    if (scrollLeft > currentTargetScroll) {
      return targetIdx + 1;
    }
    return targetIdx;
  }

  static _decrementIndexIfNeeded(targetIdx, scrollLeft, currentTargetScroll) {
    if (scrollLeft < currentTargetScroll) {
      return targetIdx - 1;
    }
    return targetIdx;
  }

  _getTargetScrollPosition(targetIdx) {
    let target = 0;
    if (targetIdx !== 0) {
      const colLeft = this.cumulativeWidths[targetIdx] || 0;
      target = colLeft - this.firstColumnWidth;
    }
    return target;
  }

  _performSnapScroll(target, targetIdx) {
    this.programmaticScroll = true;
    if (this.programmaticTimer) clearTimeout(this.programmaticTimer);

    this.container.classList.add('smooth-scroll');
    this.container.scrollTo({ left: target, behavior: 'smooth' });

    this.currentIndex = targetIdx;
    this.updateNavigation();

    this.programmaticTimer = setTimeout(() => {
      this.programmaticScroll = false;
      this.container.classList.remove('smooth-scroll');
      this.programmaticTimer = null;
      this.updateNavigationAriaLabels();
    }, 420);
  }

  debounce(fn, wait) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  handleUpEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = false;
    this.isMoveEvent = false;

    if (this.savedPositionX - getPositionX(event) < -globals.SWIPE_THRESHOLD) {
      this.scrollToColumn(this.currentIndex - 1);
    }

    if (this.savedPositionX - getPositionX(event) > globals.SWIPE_THRESHOLD) {
      this.scrollToColumn(this.currentIndex + 1);
    }

    this.updateNavigation();
  }

  handleDownEvent(event) {
    if (!event.cancelable) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = true;
    this.savedPositionX = getPositionX(event);
  }

  handleMoveEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isMoveEvent = true;
  }

  updateTableHeaderPosition() {
    const tableRect = this.container.getBoundingClientRect(); // Get table's position relative to the viewport

    if (this.navigationElement?.style.display === 'flex') {
      const navigationElementRectPosition = this.navigationElement.getBoundingClientRect();

      if (navigationElementRectPosition.top <= 0) {
        const navigationHeight = this.navigationElement.offsetHeight;

        this.thead.style.top = `${navigationHeight + tableRect.top * (-1)}px`;
        this.thead.style.position = 'sticky';
      } else {
        this.thead.style.position = 'static';
      }
    } else if (tableRect.top <= 0) {
      this.thead.style.top = `${tableRect.top * (-1)}px`;
      this.thead.style.position = 'sticky';
    } else {
      this.thead.style.position = 'static';
    }
  }
}

const initializeDynamicTable = () => {
  document.querySelectorAll(globals.DYNAMIC_TABLE_DATA_ELEMENT).forEach((dynamicTable) => new DynamicTable(dynamicTable));
};

export {
  DynamicTable,
  initializeDynamicTable,
};